<template>
    <div class="flex flex-wrap w-full leading-6">
        <label
            v-for="option in votedOptions"
            :key="option.option_id"
            class="bg-slate-50 h-12 w-full flex mb-2 relative rounded-md"
            :class="[hasVoted ? '' : 'cursor-pointer']"
        >
            <div
                :class="[option.user_vote ? 'bg-green-200' : 'bg-blue-300']"
                :style="`width: ${option.width}%`"
                class="absolute top-0 left-0 h-12 rounded-md transition-all duration-1000 ease-out"
            ></div>
            <div class="relative flex px-2 md:px-3 w-full">
                <div class="flex items-center w-full">
                    <span v-if="!hasVoted">
                        <InputCheckbox
                            v-if="poll.multi_vote"
                            :id="`${option.option_id}`"
                            v-model="checkedOptions"
                            :value="option.option_id"
                            class="mr-2"
                        >
                        </InputCheckbox>
                        <InputRadio
                            v-else
                            :id="`${option.option_id}`"
                            v-model="votedOption"
                            :value="option.option_id"
                            class="mr-2"
                            @update:model-value="prepareVote()"
                        >
                        </InputRadio>
                    </span>
                    <div class="truncatepoll px-1">{{ option.option }}</div>
                </div>
                <div
                    v-if="hasVoted"
                    class="flex items-center ml-auto"
                >
                    {{ option.vote_count }}
                </div>
            </div>
        </label>
        <div
            v-show="voterCount > 0"
            class="text-sm text-slate-400 mt-1 w-full"
        >
            {{ t('post:pollTotalVotes', { count: voterCount }) }}
        </div>
        <!-- native modifier has been removed, please confirm whether the function has been affected  -->
        <BtnCta
            v-if="poll.multi_vote && !hasVoted"
            variant="secondary"
            class="mt-1"
            :label="t('post:vote')"
            @click="prepareVote"
        />
    </div>
</template>

<script>
import InputRadio from '@/components/InputRadio';
import InputCheckbox from '@/components/InputCheckbox';
import { updatePollOptions } from '@/api/backend/post';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'PostPoll',
    components: {
        InputRadio,
        InputCheckbox,
    },
    props: {
        context: {
            type: String,
            required: true,
        },
        poll: {
            type: Object,
            required: true,
        },
    },
    setup() {
        const { t } = useTranslation('post');
        return { t };
    },
    data() {
        return {
            checkedOptions: this.poll.options.filter((item) => item.user_vote).map((item) => item.option_id),
            votedOption: (!this.poll.multi_vote && this.poll.options.find((item) => item.user_vote)?.option_id) || null,
            voterCount: this.poll.voter_count,
        };
    },
    computed: {
        votedOptions() {
            return this.poll.options.map((item) => ({
                ...item,
                width:
                    item.vote_count === 0 || !this.hasVoted ? 0 : (item.vote_count / this.totalVotes).toFixed(2) * 100,
            }));
        },
        totalVotes() {
            return this.poll.options.reduce((acc, value) => {
                return acc + value.vote_count;
            }, 0);
        },
        hasVoted() {
            return this.poll.options.some((option) => option.user_vote);
        },
    },
    methods: {
        async prepareVote() {
            let changedOptions = this.poll.multi_vote
                ? this.votedOptions.filter(
                      (option) => this.checkedOptions.includes(option.option_id) !== option.user_vote,
                  )
                : this.votedOptions.filter((option) => option.option_id === this.votedOption || option.user_vote);
            changedOptions = changedOptions.map((item) => item.option_id);

            this.updateOptions(changedOptions);

            const promises = this.poll.multi_vote
                ? changedOptions.map((option) => this.vote(option))
                : [this.vote(this.votedOption)];
            Promise.all(promises).catch((e) => {
                this.updateOptions(changedOptions);
                this.onError(e);
            });
        },
        updateOptions(options) {
            this.poll.options.forEach((option) => {
                if (options.includes(option.option_id)) {
                    option.user_vote = !option.user_vote; // eslint-disable-line no-param-reassign
                    option.vote_count = option.user_vote ? option.vote_count + 1 : option.vote_count - 1; // eslint-disable-line no-param-reassign
                }
            });
        },
        async vote(optionID) {
            await updatePollOptions(this.context, this.poll.poll_id, optionID).then((value) => {
                this.voterCount = value.voter_count;
            });
        },
        onError(error) {
            this.$store.dispatch('flashMessages/showError', {
                content: error.response?.data?.message || this.t('errors.requestError'),
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.truncatepoll {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
</style>
